// Keeps the live <head> in sync with the admin-saved SEO override for the
// current path, after hydration and after every client-side navigation.
import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";

import { applySeoOverrideToDocument } from "@/lib/seo/apply-override-dom";
import { normalizeSeoPath, type PageSeoOverride } from "@/lib/seo/page-seo";

export function SeoOverrideSync({ rows }: { rows: PageSeoOverride[] }) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const map = new Map(rows.map((r) => [normalizeSeoPath(r.path), r]));

    const apply = () => {
      applySeoOverrideToDocument(
        document,
        map.get(normalizeSeoPath(window.location.pathname)) ?? null,
      );
    };

    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      // Run after the router has committed its own head tags for this match.
      frame = requestAnimationFrame(() => {
        apply();
        frame = requestAnimationFrame(apply);
      });
    };

    apply();
    schedule();

    // The router re-renders head tags asynchronously (loaders, streaming),
    // so re-assert whenever <head> changes rather than only on navigation.
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        apply();
      });
    });
    observer.observe(document.head, { childList: true, subtree: true, attributes: true });

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [rows]);

  return null;
}

/** Subscribes head re-assertion to router navigations. */
export function useSeoOverrideNavigationSync(rows: PageSeoOverride[]) {
  const router = useRouter();
  useEffect(() => {
    if (typeof document === "undefined") return;
    const map = new Map(rows.map((r) => [normalizeSeoPath(r.path), r]));
    return router.subscribe("onResolved", () => {
      requestAnimationFrame(() =>
        applySeoOverrideToDocument(
          document,
          map.get(normalizeSeoPath(window.location.pathname)) ?? null,
        ),
      );
    });
  }, [router, rows]);
}
